import { NavLink } from "react-router-dom";

import { useAuth, type Role } from "@/auth/AuthContext";
import {
  IconAudit,
  IconBI,
  IconDashboard,
  IconDrafts,
  IconMarkets,
  IconNews,
  IconResolutions,
  IconSettings,
  IconUsers,
  IconWallet,
  IconWithdrawals,
} from "./Icon";

interface NavItem {
  to: string;
  label: string;
  icon: (props: { size?: number; className?: string }) => JSX.Element;
  /** Se settato, l'item è visibile solo agli utenti con questo ruolo o superiore. */
  minRole?: Role;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Dashboard", icon: IconDashboard },
  { to: "/markets", label: "Markets", icon: IconMarkets },
  { to: "/market-drafts", label: "Market Drafts", icon: IconDrafts },
  { to: "/news", label: "News", icon: IconNews },
  { to: "/resolutions", label: "Risoluzioni", icon: IconResolutions },
  { to: "/users", label: "Utenti", icon: IconUsers },
  { to: "/withdrawals", label: "Prelievi", icon: IconWithdrawals },
  { to: "/audit", label: "Audit Log", icon: IconAudit },
  { to: "/bi", label: "BI", icon: IconBI },
  { to: "/settings", label: "Impostazioni", icon: IconSettings, minRole: "admin" },
  {
    to: "/payment-configs",
    label: "Payment Configs",
    icon: IconWallet,
    minRole: "admin",
  },
];

export function Sidebar() {
  const auth = useAuth();

  return (
    <aside className="w-[220px] shrink-0 border-r border-line bg-bg-1 flex flex-col">
      {/* Brand block */}
      <div className="px-5 py-4 border-b border-line">
        <div className="flex items-center gap-2">
          <div className="bo-badge h-7 w-7 rounded-md" />
          <div className="leading-tight">
            <div className="text-[14px] font-semibold">Quantica</div>
            <div className="text-[10px] uppercase tracking-wider text-muted">
              Backoffice
            </div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2 py-3 flex flex-col gap-1 overflow-y-auto">
        {NAV_ITEMS.map((item) => {
          if (item.minRole === "admin" && auth.role !== "admin") return null;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-[13px] transition-colors ${
                  isActive
                    ? "bg-accent text-white"
                    : "text-text-2 hover:bg-bg-2 hover:text-text"
                }`
              }
            >
              <item.icon size={16} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* User footer */}
      <div className="px-3 py-3 border-t border-line text-[11.5px] text-muted">
        <div className="truncate" title={auth.email ?? undefined}>
          {auth.email ?? "—"}
        </div>
        <div className="mt-1.5">
          <span className="pill pill-accent">{auth.role ?? "—"}</span>
        </div>
      </div>
    </aside>
  );
}
